import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ComponentService, CreatorFrontApi, IFilterComponents } from '../../services/component.service';
import { sortByProp } from '../../utils/array';
import { fadeAnimation } from '../../animations';
import { NewApiResponse } from '../../services/new-api-http.service';
import { BeeFreeService } from '../../services/beefree.service';
import { Subscription, forkJoin } from 'rxjs';
import { OverlayService } from '../../services/overlay.service';
import { MaintenanceService } from '../../services/maintenance.service';

@Component({
	selector: 'app-component-list',
	templateUrl: './component-list.component.html',
	styleUrls: ['./component-list.component.scss'],
	animations: [fadeAnimation]
})
export class ComponentListComponent implements OnInit, OnDestroy {
	viewMode = 'list';
	total;
	componentsList;
	start;
	type;
	name;
	userId;
	orderBy;
	sortOrder;
	sortDirection;
	srv: boolean;
	currentPage;
	maxPaginationItems = 10;
	filterByOptions: Array<IFilterComponents> = [];
	isAdmin: boolean;
	mainHeight = window.innerHeight - 127;
	isBeeFree: boolean;
	joinedSubscription: Subscription;
	supportedComponentsSubscription: Subscription;
	defaultIcon = 'fa fa-exclamation-circle';

	constructor(
		private route: ActivatedRoute,
		private auth: AuthService,
		private component: ComponentService,
		private router: Router,
		private beeFree: BeeFreeService,
		private overlay: OverlayService,
		private maintenance: MaintenanceService
	) {}

	ngOnInit() {
		this.viewMode = this.route.snapshot.params['view'] ? this.route.snapshot.params['view'] : 'list';
		this.start = this.route.snapshot.params['start'] ? this.route.snapshot.params['start'] : 0;
		this.currentPage = +this.start + 1;
		this.type = this.route.snapshot.params['type'] ? this.route.snapshot.params['type'] : '';
		this.name = this.route.snapshot.params['name'] ? this.route.snapshot.params['name'] : '';
		this.srv = this.route.snapshot.params['srv'] === 'true' ? true : false;
		this.userId = this.auth.getUser() && this.auth.getUser().userId;
		const uuid = this.auth.getUser() && this.auth.getUser().uuid;
		this.isAdmin = this.auth.isAdmin();
		this.sortOrder = this.route.snapshot.params['sortOrder'] ? this.route.snapshot.params['sortOrder'] : 'updated_at';
		this.sortDirection = this.route.snapshot.params['sortDirection'] ? this.route.snapshot.params['sortDirection'] : 'DESC';
		this.orderBy = this.route.snapshot.params['sortOrder']
			? this.route.snapshot.params['sortOrder'] + this.route.snapshot.params['sortDirection']
			: 'updated_atDESC';

		const searchType = this.type === '20' || this.type === '40' ? '20,40' : this.type;
		this.fetchComponentList(searchType);

		this.getFilterOptions();
		this.component.updatePreviewContent(' ');
		this.isBeeFree = this.beeFree.isBeeFreeAddon();
	}

	ngOnDestroy() {
		this.joinedSubscription.unsubscribe();
		if (this.supportedComponentsSubscription) {
			this.supportedComponentsSubscription.unsubscribe();
		}
	}

	getFilterOptions(): void {
		this.supportedComponentsSubscription = this.component.getSupportedComponents().subscribe((response: NewApiResponse) => {
			if (response && response.status) {
				for (const item in response.data) {
					if (response.data[item]) {
						if (
							![40, 43, 45].includes(response.data[item].type) &&
							response.data[item].active &&
							((this.isAdmin && response.data[item].role_id >= 1) || (!this.isAdmin && response.data[item].role_id === 1))
						) {
							this.filterByOptions.push({ label: response.data[item].description, value: response.data[item].type });
						}
					}
				}
			}
			this.filterByOptions.sort(sortByProp('label'));
		});
	}

	pageChanged(event: any): void {
		const uuid = this.auth.getUser().uuid;
		window.scrollTo(0, 0);
		this.start = !isNaN(parseFloat(event)) && isFinite(event) ? event : event.page - 1;
		this.currentPage = +this.start + 1;
		if (this.orderBy) {
			this.sortDirection = this.orderBy.includes('DESC') ? 'DESC' : 'ASC';
			this.sortOrder = this.orderBy.replace('DESC', '').replace('ASC', '');
		}
		const urlParams = ['component-list', this.viewMode, this.start, this.type, this.name, this.srv, this.sortOrder, this.sortDirection];

		this.router.navigate(urlParams).then(() => {
			const useCombinedTypes = ['20', '24', '40', '43', '45'].includes(this.type);
			const searchType = useCombinedTypes ? this.combinedTypes(this.type) : this.type;
			this.fetchComponentList(searchType);
		});
	}

	handleOutput(event) {
		switch (event.method) {
			case 'delete':
				this.deleteComponent(event.componentId);
				break;
			case 'sendToBeeFree':
				this.sendToBeeFree(event.componentId);
				break;
		}
	}

	deleteComponent(componentId) {
		this.component
			.deleteComponent(componentId)
			.then((res) => {
				this.pageChanged(0);
			})
			.catch((err) => {
				this.overlay.updateOverlay(true, 'There was an error deleteing your component. Please try again.', 'alert');
				this.pageChanged(0);
			});
	}

	onViewModeChange(viewMode) {
		if (this.viewMode === viewMode) {
			return false;
		}
		this.viewMode = viewMode;
		this.pageChanged(0);
	}

	sendToBeeFree(componentId) {
		this.component.getComponentData(componentId).subscribe((response: CreatorFrontApi) => {
			if (response && response.status) {
				const messageData = {
					type: 'html',
					value: {
						html: response.data.snippet,
						metadata: {
							componentID: response.data.id
						}
					}
				};
				this.beeFree.sendMessage(messageData, 'onSave');
			}
		});
	}

	combinedTypes(type) {
		switch (type) {
			// Location / Store Locator
			case '20':
			case '40':
				return '20,40';
			// Custom / DCO / Sephora Timetrade
			case '24':
			case '43':
			case '45':
				return '24,43,45';
		}
	}

	fetchComponentList(searchType) {
		let suportedComponents$ = this.component.getSupportedComponents();
		let componentsList$ = this.component.fetchComponents(
			this.start,
			searchType,
			this.name,
			this.srv,
			this.sortOrder,
			this.sortDirection
		);

		this.joinedSubscription = forkJoin([componentsList$, suportedComponents$]).subscribe(
			(results) => {
				const componentListResults = results[0];
				const suportedComponentsResults = results[1];
				this.total = componentListResults.data.count;

				this.componentsList = componentListResults.data.rows.map((item) => {
					const supportedcomponentItem = suportedComponentsResults.data.find((data) => {
						return data.type === item.type;
					});

					if (supportedcomponentItem && supportedcomponentItem.icon) {
						item['icon'] = supportedcomponentItem.icon;
					} else {
						item['icon'] = this.defaultIcon;
					}
					return item;
				});
			},
			(err) => {
				this.overlay.updateOverlay(true, JSON.stringify(err), 'alert');
				this.maintenance.serverMaintenance(err);
			}
		);
	}
}
